import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import logo from '../../assets/img/logo.svg';
import { userActions, alertActions, authActions } from '../../_store';
import {
    validateEmail,
    validateIdNumber,
    validatePassword,
    validatePasswordMatch,
    validateRSANumber,
} from '../../_helpers/validate';
import './register.css';
import InputSuccessMessage from '../../_components/inputMessages/inputSuccessMessage/inputSuccessMessage';
import InputErrorMessage from '../../_components/inputMessages/inputErrorMessage/inputErrorMessage';
import PasswordValidator from '../../_components/passwordValidator/passwordValidator';
import PasswordStrengthChecker from '../../_components/passwordStrengthChecker/passwordStrengthChecker';
import ErrorMessage from '../../_components/errorMessage/errorMessage';

export { Register };

function Register() {
    // Defaults
    const dispatch = useDispatch();
    const urlParams = new URLSearchParams(window.location.search);
    const redirect_uri = urlParams.get('redirect_uri');
    const clientNameOptions = process.env.REACT_APP_CLIENT_IDS;

    // Initial setup for navigation props
    dispatch(
        authActions.setNavProps({
            title: 'NEW USER REGISTRATION',
            button: 'Login',
            action: '../login',
            status: 'Already Signed up?',
        })
    );
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [errorLogin, setErrorLogin] = useState(null);
    const [validPhoneNumber, setValidPhoneNumber] = useState(null);
    const [validIdNumber, setValidIdNumber] = useState(null);
    const [validEmail, setValidEmail] = useState(null);
    const [validPasswordMatch, setValidPasswordMatch] = useState(null);
    const [passStrData, setPassStrData] = useState(null);
    const [userPassword, setUserPassword] = useState(undefined);
    const [userPasswordConfirm, setUserPasswordConfirm] = useState(undefined);
    const [termsAgree, setTermsAgree] = useState(null);
    const [passLengthErr, setPassLengthErr] = useState(null);

    useEffect(() => {
        setValidPhoneNumber(null);
        setValidIdNumber(null);
        setValidEmail(null);
        setValidPasswordMatch(null);
        setPassStrData(null);
        setUserPassword(undefined);
        setTermsAgree(null);
        setErrorLogin(null);
    }, []);

    // form validation rules
    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .required('Email Address is required')
            .email('Email is invalid'),
        rsaId: Yup.string().required('RSA ID is required'),
        firstName: Yup.string().required('Name is required'),
        lastName: Yup.string().required('Surname is required'),
        phoneNumber: Yup.string()
            .required('Phone Number is required')
            .test((value)=> validateRSANumber(value)),
        password: Yup.string()
            .required('Password is required')
            .min(8, 'Password must be at least 8 characters')
            .matches(
                /(?=.*[a-z])(?=.*[A-Z])|(?=.*[0-9])|(?=.*[!@#$%^&*])/,
                'Password must contain at least two of the following: uppercase letters, lowercase letters, numbers, special characters'
            ),
        confirmPassword: Yup.string().oneOf(
            [Yup.ref('password'), null],
            'Passwords must match'
        ),
    });
    const formOptions = { resolver: yupResolver(validationSchema) };

    // get functions to build form with useForm() hook
    const { register, handleSubmit, clearErrors, formState } =
        useForm(formOptions);
    const { errors, isSubmitting } = formState;

    async function onSubmit(data) {
        dispatch(alertActions.clear());
        setPassLengthErr(false);
        setErrorLogin(null);
        if (
            termsAgree === false ||
            termsAgree === null ||
            termsAgree === undefined
        ) {
            setTermsAgree(false);
            return false;
        }

        if (passStrData && passStrData.contains.length !== 4) {
            setPassLengthErr(true);
            return null;
        }

        const registrationObj = {
            email: String(data.email).toLowerCase(),
            IdNumber: data.rsaId,
            firstName: data.firstName,
            lastName: data.lastName,
            cellphone: data.phoneNumber,
            password: data.password,
            username: String(data.email).toLowerCase().trim(),
            clientName: checkClientName(),
        };

        try {
            const response = await dispatch(
                userActions.register(registrationObj)
            ).unwrap();

            if (response && response.statusCode === 400) {
                setErrorLogin(response.message);
            }
        } catch (error) {
            dispatch(alertActions.error(error));
        }
    }

    const checkClientName = () => {
        if (
            clientNameOptions !== null &&
            clientNameOptions !== undefined &&
            clientNameOptions !== '' &&
            redirect_uri !== null &&
            redirect_uri !== undefined &&
            redirect_uri !== ''
        ) {
            const clientNamesObj = JSON.parse(clientNameOptions);

            const filtered = clientNamesObj.clientNames.filter((client) => {
                return redirect_uri.includes(client.name);
            });

            if (filtered.length > 0) {
                return filtered[0].value;
            } else {
                return 'Splendi';
            }
        } else {
            return 'Splendi';
        }
    };

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };

    const validateInputs = (e) => {
        const { name, value } = e.target;
        let valid;

        switch (name) {
            case 'phoneNumber':
                 if (!/^\d*$/.test(value)) {
                e.target.value = value.replace(/\D/g, '');
                return;
            }
                valid = validateRSANumber(value);
                setValidPhoneNumber(valid);
                break;
            case 'email':
                valid = validateEmail(value);
                setValidEmail(valid);
                break;
            case 'rsaId':
                valid = validateIdNumber(value);
                setValidIdNumber(valid);
                break;
            case 'password':
                const strengthCheckerData = validatePassword(value);
                setPassStrData(strengthCheckerData);
                setUserPassword(value);

                if (
                    userPasswordConfirm !== null &&
                    userPasswordConfirm !== undefined &&
                    userPasswordConfirm !== ''
                ) {
                    valid = validatePasswordMatch(userPasswordConfirm, value);
                    valid
                        ? setValidPasswordMatch(true)
                        : setValidPasswordMatch(false);
                }
                break;
            case 'confirmPassword':
                valid = validatePasswordMatch(userPassword, value);
                setValidPasswordMatch(valid);
                setUserPasswordConfirm(value);
                break;
            default:
                break;
        }
    };

    return (
        <div className='card mb-5 no-border'>
            <div className='card-body px-md-5 pt-5 pb-5 register-card-body'>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='row mb-4'>
                        <div className='col-md-6'>
                            <div className='mb-3'>
                                <h3 className='registration-header'>
                                    Registration
                                </h3>
                                <p className='mt-1 mb-1'>
                                    Please complete our 3-step registration
                                    process to view your credit profile.
                                </p>
                            </div>
                            {/* error message */}
                            {errorLogin ? (
                                <div className='mb-3'>
                                    <ErrorMessage
                                        message={errorLogin}
                                        contact='Please contact info@splendi.co.za for assistance.'
                                    />
                                </div>
                            ) : null}
                        </div>
                        <div className='col-md-6 text-left'>
                            <div className='mb-3'>
                                <img
                                    src={logo}
                                    alt='Logo'
                                    className='img-fluid'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-md-6'>
                            <div className='row'>
                                {/* EMAIL */}
                                <div className='mb-4'>
                                    <label className='form-label fw-bold'>
                                        Email Address *
                                    </label>
                                    <input
                                        name='email'
                                        type='text'
                                        {...register('email')}
                                        className={`form-control ${
                                            errors.email ? 'is-invalid' : ''
                                        }`}
                                        onChange={(e) => {
                                            validateInputs(e);
                                            clearErrors('email');
                                        }}
                                    />
                                </div>
                                {validEmail === true ? (
                                    <InputSuccessMessage
                                        message={'Email is valid'}
                                    />
                                ) : null}
                                {validEmail === false ? (
                                    <InputErrorMessage
                                        message={'Email is not valid!'}
                                    />
                                ) : null}
                            </div>
                            <div className='row'>
                                {/* ID Number */}
                                <div className='mb-4'>
                                    <label className='form-label fw-bold'>
                                        RSA ID *
                                    </label>
                                    <input
                                        name='rsaId'
                                        type='number'
                                        maxLength={13}
                                        {...register('rsaId')}
                                        className={`form-control ${
                                            errors.rsaId ? 'is-invalid' : ''
                                        }`}
                                        onChange={(e) => {
                                            validateInputs(e);
                                            clearErrors('rsaId');
                                        }}
                                    />
                                </div>
                                {validIdNumber === true ? (
                                    <InputSuccessMessage
                                        message={'ID number is valid'}
                                    />
                                ) : null}
                                {validIdNumber === false ? (
                                    <InputErrorMessage
                                        message={'ID number is not valid!'}
                                    />
                                ) : null}
                            </div>
                            <div className='row'>
                                {/* Name */}
                                <div className='mb-4'>
                                    <label className='form-label fw-bold'>
                                        Name *
                                    </label>
                                    <input
                                        name='firstName'
                                        type='text'
                                        {...register('firstName')}
                                        className={`form-control ${
                                            errors.firstName ? 'is-invalid' : ''
                                        }`}
                                        onChange={(e) => {
                                            validateInputs(e);
                                            clearErrors('firstName');
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row'>
                                {/* Surname */}
                                <div className='mb-4'>
                                    <label className='form-label fw-bold'>
                                        Surname *
                                    </label>
                                    <input
                                        name='lastName'
                                        type='text'
                                        {...register('lastName')}
                                        className={`form-control ${
                                            errors.lastName ? 'is-invalid' : ''
                                        }`}
                                        onChange={(e) => {
                                            validateInputs(e);
                                            clearErrors('lastName');
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row'>
                                {/* Phone Number */}
                                <div className='mb-4'>
                                    <label className='form-label fw-bold'>
                                        Mobile Number *
                                    </label>
                                    <input
                                        name='phoneNumber'
                                        type='text'
                                        {...register('phoneNumber')}
                                        className={`form-control ${
                                            errors.phoneNumber
                                                ? 'is-invalid'
                                                : ''
                                        }`}
                                        onChange={(e) => {
                                            validateInputs(e);
                                            clearErrors('phoneNumber');
                                        }}
                                    />
                                    {validPhoneNumber === false ? (
                                          <small className="form-text text-muted">
                                          Format: Enter a 10-digit mobile number (e.g., 0833554458)
                                      </small>
                                    ) : null }
                                  
                                </div>
                                {validPhoneNumber === true ? (
                                    <InputSuccessMessage
                                        message={'Phone number is valid'}
                                    />
                                ) : null}
                                {validPhoneNumber === false ? (
                                    <InputErrorMessage
                                        message={'Please enter a valid 10-digit mobile number without spaces or symbols.'}
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className='col-md-6'>
                            <div className='row'>
                                {/* Password */}
                                <div className='mb-4'>
                                    <label className='form-label fw-bold '>
                                        Password *
                                    </label>
                                    <div className='password-input-container'>
                                        <input
                                            name='password'
                                            type={
                                                passwordVisible
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            {...register('password')}
                                            className='form-control'
                                            onChange={(e) => {
                                                clearErrors('password');
                                                setPassLengthErr(false);
                                                if (
                                                    e.target.value.includes(' ')
                                                ) {
                                                    return null;
                                                } else {
                                                    validateInputs(e);
                                                }
                                            }}
                                            value={userPassword}
                                        />
                                        <span
                                            className='password-toggle-icon'
                                            onClick={togglePasswordVisibility}
                                        >
                                            {passwordVisible ? (
                                                <FaEyeSlash />
                                            ) : (
                                                <FaEye />
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                {/* Confirm Password */}
                                <div className='mb-4'>
                                    <label className='form-label fw-bold'>
                                        Confirm Password *
                                    </label>
                                    <div className='password-input-container'>
                                        <input
                                            name='confirmPassword'
                                            type={
                                                confirmPasswordVisible
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            {...register('confirmPassword')}
                                            className={`form-control ${
                                                errors.confirmPassword
                                                    ? 'is-invalid'
                                                    : ''
                                            }`}
                                            onChange={(e) => {
                                                clearErrors('confirmPassword');
                                                if (
                                                    e.target.value.includes(' ')
                                                ) {
                                                    return null;
                                                } else {
                                                    validateInputs(e);
                                                }
                                            }}
                                            value={userPasswordConfirm}
                                        />
                                        <span
                                            className='password-toggle-icon'
                                            onClick={
                                                toggleConfirmPasswordVisibility
                                            }
                                        >
                                            {confirmPasswordVisible ? (
                                                <FaEyeSlash />
                                            ) : (
                                                <FaEye />
                                            )}
                                        </span>
                                    </div>
                                </div>
                                {validPasswordMatch === true ? (
                                    <InputSuccessMessage
                                        message={'The passwords are matching!'}
                                    />
                                ) : null}
                                {validPasswordMatch === false ? (
                                    <InputErrorMessage
                                        message={'The passwords do not match!'}
                                    />
                                ) : null}
                                {passLengthErr === true ? (
                                    <InputErrorMessage
                                        message={
                                            'Password has not fulfilled the requirements!'
                                        }
                                    />
                                ) : null}
                            </div>
                            <div className='row mb-4'>
                                {/* Password Strength Checker */}
                                <PasswordStrengthChecker
                                    passwordData={passStrData}
                                />
                            </div>
                            <div className='row mb-4'>
                                {/* Password Validator */}
                                <PasswordValidator passwordData={passStrData} />
                            </div>
                            <div className='row'>
                                {/* Terms & Conditions */}
                                <div className='mb-5'>
                                    <div className='d-grid gap-2'>
                                        <div className='form-check'>
                                            <input
                                                type='checkbox'
                                                id='terms'
                                                name='confirmTerms'
                                                className='form-check-input'
                                                onChange={(e) =>
                                                    setTermsAgree(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <label
                                                className='form-check-label'
                                                htmlFor='terms'
                                            >
                                                I hereby accept the{' '}
                                                <span className='primary'>
                                                    <a
                                                        href='https://www.splendi.co.za/terms-and-conditions'
                                                        target='_blank'
                                                        rel='noreferrer'
                                                    >
                                                        Terms of Use
                                                    </a>
                                                </span>{' '}
                                                and{' '}
                                                <span className='primary'>
                                                    <a
                                                        href='https://www.splendi.co.za/privacy_policy'
                                                        target='_blank'
                                                        rel='noreferrer'
                                                    >
                                                        Privacy Policy
                                                    </a>
                                                </span>{' '}
                                                and give consent for Splendi to
                                                retrieve my Credit Report from
                                                XDS Credit Bureau.
                                            </label>
                                        </div>

                                        {termsAgree === false ? (
                                            <InputErrorMessage
                                                message={
                                                    'You must agree to the Terms & Conditions to proceed'
                                                }
                                            />
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                {/* Register Button */}
                                <div className='mb-3'>
                                    <div className='d-grid gap-2'>
                                        <button
                                            disabled={isSubmitting}
                                            className='btn btn-primary'
                                        >
                                            {isSubmitting && (
                                                <span className='spinner-border spinner-border-sm me-1'></span>
                                            )}
                                            Register
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
